import { ApiError, createHttpClient, encodeUtf8HeaderValue } from '@wanjianshi/shared-http';
import { createUuid } from '@wanjianshi/shared-utils';
import { adminEnvironment } from '@/config/environment';

const CSRF_COOKIE = 'wjs_admin_csrf';

function readCookie(name: string): string {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
}

function readOrCreateCsrfToken(): string {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;

  // This only repairs sessions issued before the server started setting the
  // readable CSRF cookie. The backend still requires the matching cookie and
  // header, so the token never bypasses CSRF validation.
  const token = createUuid();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(CSRF_COOKIE)}=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secure}`;
  return token;
}

let unauthorizedHandler: (() => void) | undefined;
let unauthorizedRecoveryHandler: (() => Promise<boolean>) | undefined;
let forbiddenHandler: ((message: string) => void) | undefined;

export function configureAdminHttpHandlers(handlers: {
  recoverUnauthorized?: () => Promise<boolean>;
  onUnauthorized?: () => void;
  onForbidden?: (message: string) => void;
}) {
  unauthorizedRecoveryHandler = handlers.recoverUnauthorized;
  unauthorizedHandler = handlers.onUnauthorized;
  forbiddenHandler = handlers.onForbidden;
}

export const adminApi = createHttpClient({
  baseUrl: adminEnvironment.apiBaseUrl,
  timeoutMs: adminEnvironment.requestTimeoutMs,
  auth: {
    apply(init) {
      const headers = new Headers(init.headers);
      const method = String(init.method ?? 'GET').toUpperCase();
      if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        const csrfToken = readOrCreateCsrfToken();
        if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
      }
      // 后台查询包含实时业务和权限数据，不允许浏览器复用历史响应。
      const cache = ['GET', 'HEAD'].includes(method) ? 'no-store' : init.cache;
      return { ...init, headers, credentials: 'include', cache };
    },
    recoverUnauthorized() {
      return unauthorizedRecoveryHandler?.() ?? Promise.resolve(false);
    },
    onUnauthorized() {
      unauthorizedHandler?.();
    },
    onForbidden({ error }) {
      forbiddenHandler?.(error.message);
    },
  },
});

/**
 * 401 已由统一认证处理器接管：清理后台身份并由路由守卫跳转登录页。
 * 页面层应直接结束当前操作，避免再显示“未登录”等重复错误。
 */
export function isAdminUnauthorizedError(reason: unknown): reason is ApiError {
  return reason instanceof ApiError && reason.status === 401;
}

export function createIdempotencyKey(prefix = 'admin'): string {
  return `${prefix}-${createUuid()}`;
}

export function adminOperationOptions(
  reason?: string,
  idempotencyKey?: string,
): { headers: Headers } {
  const headers = new Headers();
  if (reason?.trim()) {
    headers.set('X-Operation-Reason', encodeUtf8HeaderValue(reason.trim()));
  }
  if (idempotencyKey) headers.set('X-Idempotency-Key', idempotencyKey);
  return { headers };
}
